import axios from "src/utils/axios";
import {
  AssociationPortalAdmin,
  AssociationPortalConfiguration,
  AssociationPortalManifest,
  PortalAssetType,
  PortalAssetUpload,
} from "src/types/association-portal";

export async function getAssociationPortalBySlug(
  providerUrltag: string,
): Promise<AssociationPortalManifest> {
  const response = await axios.get<AssociationPortalManifest>(
    `/providers/public/portals/by-slug/${encodeURIComponent(providerUrltag)}`,
  );
  return response.data;
}

export async function resolveAssociationPortalHostname(
  hostname: string,
): Promise<AssociationPortalManifest> {
  const response = await axios.get<AssociationPortalManifest>(
    "/providers/public/portals/resolve",
    { params: { hostname } },
  );
  return response.data;
}

export async function getAssociationPortalAdmin(
  providerId: number,
): Promise<AssociationPortalAdmin> {
  const response = await axios.get<AssociationPortalAdmin>(
    `/providers/${providerId}/portal`,
  );
  return response.data;
}

export async function saveAssociationPortal(
  providerId: number,
  configuration: AssociationPortalConfiguration,
): Promise<AssociationPortalAdmin> {
  const response = await axios.put<AssociationPortalAdmin>(
    `/providers/${providerId}/portal`,
    configuration,
  );
  return response.data;
}

export async function setAssociationPortalEnabled(
  providerId: number,
  enabled: boolean,
): Promise<AssociationPortalAdmin> {
  const response = await axios.post<AssociationPortalAdmin>(
    `/providers/${providerId}/portal/${enabled ? "enable" : "disable"}`,
  );
  return response.data;
}

export async function uploadAssociationPortalAsset(
  providerId: number,
  assetType: PortalAssetType,
  file: File,
): Promise<PortalAssetUpload> {
  // Match the API's PNG limit before sending multipart data through a proxy.
  if (file.size > 4 * 1024 * 1024) {
    return Promise.reject({ detail: "portal_asset_too_large" });
  }
  if (file.type !== "image/png") {
    return Promise.reject({ detail: "portal_asset_must_be_png" });
  }
  const formData = new FormData();
  formData.append("file", file);
  const response = await axios.post<PortalAssetUpload>(
    `/providers/${providerId}/portal/assets/${assetType}`,
    formData,
  );
  return response.data;
}

export async function requestAssociationPortalDomain(
  providerId: number,
  hostname: string,
): Promise<AssociationPortalAdmin> {
  const response = await axios.post<AssociationPortalAdmin>(
    `/providers/${providerId}/portal/domain-request`,
    { hostname },
  );
  return response.data;
}

export async function recordAssociationPortalAnalytics(
  portalId: number,
  blockId: string,
  eventType: "portal_view" | "block_view" | "cta_click",
) {
  await axios.post(`/providers/public/portals/${portalId}/analytics`, {
    block_id: blockId,
    event_type: eventType,
    analytics_consent: true,
  });
}

export async function getAssociationPortalAnalytics(
  providerId: number,
  days = 30,
) {
  const response = await axios.get<
    Array<{ day: string; block_id: string; event_type: string; count: number }>
  >(`/providers/${providerId}/portal/analytics`, { params: { days } });
  return response.data;
}
